// @flow

import * as React from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import decode from 'lean-he/decode';

import colors from '../../Styles/colors';
import { absoluteFull } from '../../Styles/utilities';
import CoveImage from "../../ShowPage/components/CoveImage";
import PassportLabel from "./PassportLabel";
import { PlayButton } from "./PlayButton";
import AddToFavorites from "./AddToFavorites";
import TPTOriginal from "./TPTOriginal";
import ResumePlayback from "./ResumePlayback";
import VideoDetails from "./VideoDetails";
import VideoDuration from "./VideoDuration";
import Label from "./Label";
import { event } from "./../helpers";

import flag from "../../Feature/Feature";
import PBSImage from "./PBSImage";

const teaserWrapper = css`
  padding: 0 4px;
`;

const MiniTitle = styled('div')`
  position: absolute;
  top: 80%;
  width: 100%;
  padding: 8px;
  color: ${colors.coreColor2};
  text-align: center;
  white-space: normal;
  opacity: 1;
  transition: opacity 600ms cubic-bezier(0.5, 0, 0.1, 1) 400ms;
  transform: translate(0, -50%) scale(0.9999);
  font-weight: 800;
  line-height: 1.25;
  text-transform: uppercase;
  -webkit-font-smoothing: antialiased;
`;

const VideoTeaserHover = styled('div')`
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  visibility: hidden;
  transform: scale(0.5);
  transition: transform 400ms cubic-bezier(0.5, 0, 0.1, 1), visibility 0s linear 400ms;
  box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.5);
  pointer-events: none;

  & > & {
    transform: none;
  }
`;

const MiniVideoTeaserTitle = styled('div')`
  position: absolute;
  top: 80%;
  width: 100%;
  padding: 8px;
  color: #fff;
  text-align: center;
  white-space: normal;
  opacity: 1;
  transition: opacity 600ms cubic-bezier(0.5, 0, 0.1, 1) 400ms;
  transform: translate(0, -50%) scale(0.9999);
  font-size: 16px;
  line-height: 1.25;
  -webkit-font-smoothing: antialiased;
`;

const VideoTeaserInfo = styled('div')`
  padding: 4px 8px;
  color: #fff;
  font-weight: bold;
  z-index: 5;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0;
  transform: scale(0.9999);
  transition: opacity 250ms cubic-bezier(0.5, 0, 0.1, 1);
  display: flex;
  flex-direction: column;
`;

const VideoTeaserExtras = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0;
  transform: scale(0.9999);
  transition: opacity 250ms cubic-bezier(0.5, 0, 0.1, 1);
  display: flex;
  flex-direction: column;
`;

const videoTeaserWrapper = css`
  position: relative;
  padding: 0 4px;
  transition: transform 400ms cubic-bezier(0.5, 0, 0.1, 1);
  transform: translate(0, 0);
  cursor: pointer;

  &.start {
    ${VideoTeaserHover} {
      left: 0;
      transform-origin: left;
    }
  }

  &.end {
    ${VideoTeaserHover} {
      left: -100%;
      transform-origin: right;
    }
  }
  
  .parasol-container:not(.animating) & {
    &.hover {
      z-index: 4;

      & ${MiniTitle}, & ${MiniVideoTeaserTitle} {
        transition-delay: 0s;
        opacity: 0;
      }

      ${VideoTeaserHover} {
        visibility: visible;
        transform: scale(1);
        transition: transform 400ms cubic-bezier(0.5, 0, 0.1, 1), visibility 400ms linear 0s;
        pointer-events: all;
      }

      ${VideoTeaserInfo}, ${VideoTeaserExtras} {
        opacity: 1;
        transition-delay: 250ms;
        transition-duration: 600ms;
      }
    }
  }
`;

const boundsClip = css`
  ${absoluteFull()};
  overflow: hidden;
`;

const videoTeaser = css`
  position: relative;
  padding-bottom: 56.25%;
  background: #111;
`;

const videoTeaserLink = css`
  position: absolute;
  width: 100%;
  height: 100%;
  
  .home-rows-no-keyboard & {
    outline: none;
  }
`;

const videoTeaserImage = css`
  ${absoluteFull()};
  right: 0;
  bottom: 0;
  transform: scale(0.9999);
`;

const videoTeaserPreload = css` display: none; `;
const videoTeaserOverlay = css`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0) 0%,rgba(0,0,0,0.85) 100%);
  transform: scale(1.01);
  `;
const videoTeaserHoverOverlay = css`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0) 0%,rgba(0,0,0,0.9) 85%);
  transform: scale(0.9999);
`;
const videoTeaserExpiration = css`
  position: absolute;
  top: 4px;
  right: 0;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-weight: bold;
  text-transform: uppercase;
  z-index: 5;
  `;
const videoTeaserPlayButton = css`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  `;
const videoTeaserTitle = css`
  width: 100%;
  padding: 0 64px 16px;
  color: ${colors.coreColor2};
  text-align: center;
  line-height: 1;
  white-space: normal;
  `;
const showTitle = css`
  z-index: 5;
  -webkit-font-smoothing: antialiased;
  margin-bottom: 4px;
  font-weight: 800;
  line-height: 1.25;
  text-transform: uppercase;
  `;
const videoTitle = css`
  z-index: 5;
  -webkit-font-smoothing: antialiased;
  line-height: 1.25;
  `;

type Show = {
  title: string,
  mezzanine: string,
  is_local: bool
};

type Video = {
  id: string,
  title: string,
  default_url: string,
  mezzanine: string,
  images: Array<Image>,
  minimum_access_level: number,
  is_passport: bool,
  availabilities: {
    public: AvailabilityWindow,
    all_members: AvailabilityWindow,
    station_members: AvailabilityWindow
  },
  tp_media_id: number,
};

type Image = {
  profile: string,
  image: string
};

type AvailabilityWindow = {|
  start: ?number,
    end: ?number
      |};

const ONE_DAY = 60 * 60 * 24;
const EXPIRATION_TRIGGER = ONE_DAY * 7;

function time(): number {
  return Math.floor(Date.now() / 1000);
}

function getWindowKey(accessLevel: number): string {
  switch (accessLevel) {
    case 0:
      return 'public';
    case 1:
      return 'all_members';
    case 2:
      return 'station_members';
    default:
      return '';
  }
}

function getWindow(accessLevel: number, video: Video): ?AvailabilityWindow {
  let windowKey = getWindowKey(accessLevel);
  return video.availabilities && video.availabilities[windowKey] || null;
}

function getExpiration(video: Video): ?number {
  let window = getWindow(video.minimum_access_level, video);
  return window && window.end || null;
}

function isExpiring(video: Video): bool {
  let expiration = getExpiration(video);
  let now = time();

  if (expiration == null) {
    return false;
  } else {
    return (expiration - now) < EXPIRATION_TRIGGER;
  }
}

function humanExpiration(video: Video): ?number {
  if (isExpiring(video)) {
    let expiration = getExpiration(video);
    let now = time();

    if (expiration != null) {
      return Math.ceil((expiration - now) / ONE_DAY);
    }
  }

  return null;
}

function dayIndicator(count: number): string {
  return count === 1 ? 'Day' : 'Days';
}

/**
 * Filters out extraneous title parts that are commonly seen
 *
 * @param {string} title
 * @returns {string}
 */
function filterTitle(title: string): string {
  title = title.replace(/^FULL EPISODE: /, '');
  title = title.replace(/ - PBS NewsHour full episode$/i, '');
  title = title.replace(/^Amanpour: /i, '');

  return title;
}

function isNumber(n: ?number): boolean {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

export class VideoPlaceholder extends React.PureComponent<any> {
  render() {
    return <div css={teaserWrapper}>
      <div css={videoTeaser} />
    </div>;
  }
}

type ImageVariant = 'SHOW' | 'EPIOSDE';
type TitleVariant = 'SHOW' | 'EPISODE' | 'DETAILS';

type VideoProps = {|
  open: bool,
    hoverPos: ?number,
      viewPosition: ?number,
        page: number,
          pageSize: number,
            favorite: bool,
              addFavorite: Function,
                removeFavorite: Function,
                  onMouseEnter ?: Function,
                  onMouseLeave ?: Function,
                  onClose ?: Function,
                  show: Show,
                    full_length: Video,
                      miniImage: ImageVariant,
                        miniTitle: TitleVariant,
                          trackingLabelPrefix: ?string,
  // TODO: Add innerRef type
|};

type VideoState = {|
  progress: number
    |};

function trackEvent(label: string): string => void {
  return function (): void {
    event('ClickVideo')(label);
  }
}

export default class VideoTeaser extends React.PureComponent<VideoProps, VideoState> {
  constructor(props: VideoProps) {
    super(props);

    this.state = {
      progress: 0
    };

    this.closePreview = this.closePreview.bind(this);
  }

  className() {
    let { hoverPos, viewPosition, pageSize } = this.props;
    let className = '';

    if (viewPosition !== null && viewPosition !== undefined && isNumber(viewPosition)) {
      if (hoverPos === viewPosition) {
        className = `${className} hover`;
      }

      className = `${className} view-${viewPosition}`;

      if (viewPosition === 0) {
        className = `${className} start`;
      }

      if (viewPosition === pageSize - 1) {
        className = `${className} end`;
      }
    }

    return className;
  }

  style() {
    let { hoverPos, viewPosition, pageSize } = this.props;
    let styles = {};

    if (viewPosition !== null && viewPosition !== undefined && isNumber(viewPosition)) {
      if (hoverPos !== viewPosition && hoverPos !== null && hoverPos !== undefined && isNumber(hoverPos)) {
        let size = Math.floor(this.computeSize());

        if (hoverPos === 0) {
          styles.transform = `translate(${size}px, 0)`;
        } else if (hoverPos === pageSize - 1) {
          styles.transform = `translate(-${size}px, 0)`;
        } else if (viewPosition < hoverPos) {
          styles.transform = `translate(-${Math.floor(size / 2)}px, 0)`;
        } else if (viewPosition > hoverPos) {
          styles.transform = `translate(${Math.floor(size / 2)}px, 0)`;
        }
      }
    }

    return styles;
  }

  computeSize() {
    return (window.outerWidth * 0.92) / this.props.pageSize - 8;
  }

  closePreview: Function;
  closePreview(e: SyntheticTransitionEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget && this.props.onClose) {
      // this.props.onClose();
    }
  }

  renderPreviewClosed() {
    return <React.Fragment>
      <div css={boundsClip}>
      </div>
      <div css={boundsClip}>
        <VideoTeaserInfo />
      </div>
      <VideoTeaserExtras />
    </React.Fragment>;
  }

  renderPreviewOpen() {
    let { show, full_length } = this.props;
    let expiresIn = humanExpiration(full_length);

    return <React.Fragment>
      <div css={boundsClip}>
        <PBSImage
          alt={`${show.title} - ${full_length.title}`}
          aspectRatio={0.5625}
          emoCSS={videoTeaserImage}
          imageSizes={{ min: 100, max: 700, step: 150 }}
          sizes={[
            { breakpoint: '1401px', width: '34vw' },
            { breakpoint: '1101px', width: '40vw' },
            { breakpoint: '801px', width: '50vw' },
            { breakpoint: '501px', width: '66vw' },
            { breakpoint: '0', width: '100vw' }]}
          url={full_length.mezzanine} />
        <div css={videoTeaserHoverOverlay} />
      </div>
      <div css={boundsClip}>
        <VideoTeaserInfo>
          <div css={videoTeaserPlayButton}>
            <PlayButton />
          </div>
          <div css={videoTeaserTitle}>
            <div css={showTitle} className="body-normal">{decode(show.title)}</div>
            {
              show.title.toLowerCase() !== filterTitle(full_length.title).toLowerCase() &&
              <div css={videoTitle} className="body-large">{decode(filterTitle(full_length.title))}</div>
            }
          </div>
          {full_length.is_passport && <PassportLabel />}
          {
            expiresIn !== null &&
            <div css={videoTeaserExpiration}>
              <span className="expiration-text">
                <i className="far fa-clock" /> {expiresIn} {dayIndicator(expiresIn || 0)}
              </span>
            </div>
          }
          {expiresIn === null && <Label vertical="top" horizontal="right"><VideoDetails {...this.props} /></Label>}
          <VideoDuration {...this.props} />
        </VideoTeaserInfo>
      </div>
      <VideoTeaserExtras>
        {flag('favorites_list') &&
          <AddToFavorites
            isFav={this.props.favorite}
            add={() => this.props.addFavorite(full_length.id)}
            del={() => this.props.removeFavorite(full_length.id)} /> || null
        }
        {this.state.progress > 0 && <ResumePlayback progress={this.state.progress} /> || null}
      </VideoTeaserExtras>
    </React.Fragment>;
  }

  getEventLabel(): string {
    let { full_length, page, viewPosition, trackingLabelPrefix } = this.props;

    if (full_length) {
      let trackingParts = [
        full_length.title,
        full_length.tp_media_id,
        page,
        viewPosition + 1
      ];

      if (typeof trackingLabelPrefix === 'string') {
        trackingParts.unshift(trackingLabelPrefix);
      }

      return trackingParts.join(' | ');
    }

    return 'MissingVideoData';
  }

  render() {
    let { open, hoverPos, viewPosition, show, full_length, miniImage, miniTitle } = this.props;

    if (!full_length) {
      return null;
    }

    let inView = viewPosition !== null;
    let hovered = viewPosition === hoverPos;
    let miniImageUrl = miniImage === 'SHOW' && show.mezzanine || full_length.mezzanine;

    return <div css={videoTeaserWrapper} className={`video-teaser-wrapper ${this.className()}`} onMouseEnter={this.props.onMouseEnter} onMouseMove={this.props.onMouseEnter} onMouseLeave={this.props.onMouseLeave} style={this.style()}>
      <div css={videoTeaser}>
        {/* $FlowFixMe */}
        <a css={videoTeaserLink} onClick={trackEvent(this.getEventLabel())} ref={this.props.innerRef} href={full_length.default_url} tabIndex={inView ? 0 : -1} aria-hidden={inView ? "false" : "true"} aria-label={`${show.title} - ${full_length.title}`}>
          <PBSImage
            alt={`${show.title} - ${full_length.title}`}
            aspectRatio={0.5625}
            emoCSS={videoTeaserImage}
            imageSizes={{ min: 250, max: 350, step: 100 }}
            sizes={[]}
            url={miniImageUrl} />
          <div css={videoTeaserOverlay} />
          {
            miniTitle === 'SHOW' &&
            <MiniTitle aria-hidden="true">
              {decode(show.title)}
            </MiniTitle>
          }
          {
            miniTitle === 'DETAILS' &&
            <MiniTitle aria-hidden="true">
              <VideoDetails {...this.props} />
            </MiniTitle>
          }
          {
            miniTitle === 'EPISODE' &&
            <MiniVideoTeaserTitle aria-hidden="true">
              {decode(filterTitle(full_length.title))}
            </MiniVideoTeaserTitle>
          }
          {show.is_local && <TPTOriginal />}
          <VideoTeaserHover className="video-teaser-hover" onTransitionEnd={open && !hovered ? this.closePreview : null}>
            {open ? this.renderPreviewOpen() : this.renderPreviewClosed()}
          </VideoTeaserHover>
          {
            inView && <PBSImage
              ariaHidden={true}
              alt="image-preloader"
              aspectRatio={0.5625}
              emoCSS={videoTeaserPreload}
              imageSizes={{ min: 100, max: 700, step: 150 }}
              sizes={[
                { breakpoint: '1401px', width: '34vw' },
                { breakpoint: '1101px', width: '40vw' },
                { breakpoint: '801px', width: '50vw' },
                { breakpoint: '501px', width: '66vw' },
                { breakpoint: '0', width: '100vw' }]}
              url={full_length.mezzanine} />
          }
        </a>
      </div>
    </div>;
  }
}

// $FlowFixMe
VideoTeaser.defaultProps = {
  miniImage: 'EPISODE',
  miniTitle: 'SHOW'
};
